import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash';

import type { AccessibleOU, FuelUserAccess } from '../../models/FuelUserAccess';
import { Permissions } from '../../models/Permissions';
import type { Stage } from '../../models/Stage';
import { Fetched, NotStarted, OngoingFetch } from '../../models/Stage';
import type { IDSUser } from '../../models/user';
import { FuelPortalService } from '../../services/fuel-portal/fuel-portal-service';
import { LocalStorageService } from '../../services/local-storage';
import type { RootState } from '.';

export type FuelUserAccessState = {
  userAccessLoadingLoading: boolean;
  stage: Stage;
  hasErrors: boolean;
  permissions: Permissions;
  fuelUserAccessData: FuelUserAccess | any;
  accessibleOUs: AccessibleOU[];
};

export const initialState: FuelUserAccessState = {
  userAccessLoadingLoading: false,
  stage: new NotStarted(),
  hasErrors: false,
  permissions: null,
  fuelUserAccessData: null,
  accessibleOUs: [],
};

const fuelUserAccessSlice = createSlice({
  name: 'fuelUserAccess',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.userAccessLoadingLoading = true;
    },
    saveStage: (state, action: any) => {
      state.stage = action.payload;
    },
    savePermissions: (state, action: any) => {
      state.permissions = action.payload;
      state.userAccessLoadingLoading = false;
    },
    saveFuelUserAccessData: (state, action: any) => {
      state.fuelUserAccessData = action.payload;
      state.accessibleOUs = state.fuelUserAccessData.accessibleOUs;
      state.userAccessLoadingLoading = false;
    },
  },
  extraReducers: {},
});

export const { saveStage, startLoading, savePermissions, saveFuelUserAccessData } = fuelUserAccessSlice.actions;

// export fuelUserAccessSlice selector to get the slice in any component
export const fuelUserAccessSliceSelector = (state: RootState) => state.fuelUserAccess;
// export The reducer
const fuelUserAccessReducer = fuelUserAccessSlice.reducer;
export default fuelUserAccessReducer;

export const fetchFuelUserAccess = createAsyncThunk(
  // action type string
  'fuelUserAccess/fetchFuelUserAccess',
  // callback function
  async (user: IDSUser, { getState, dispatch }) => {
    dispatch(startLoading());
    const state = getState() as RootState;
    const { stage } = state.fuelUserAccess;
    const executeNewFetch: (key: string) => Promise<Permissions> = (key) => {
      // console.log('key', key)
      const promise = FuelPortalService.getUserAccess().then((response: FuelUserAccess) => {
        // console.log('getting user access', response);
        if (response) {
          let loggedInOUId: string = null;
          const defaultLoginOu = LocalStorageService.getItem('defaultLoginOu');

          if (defaultLoginOu) {
            const index = _.findIndex(response.accessibleOUs, (a) => a.ouId === defaultLoginOu);
            if (index > -1) {
              loggedInOUId = defaultLoginOu;
            }
          }
          if (!loggedInOUId && response.defaultLoginOu) {
            const index = _.findIndex(response.accessibleOUs, (a) => a.ouId === response.defaultLoginOu);
            if (index > -1 && response.defaultLoginOu) {
              loggedInOUId = response.defaultLoginOu;
              // console.log('setting defaultLoginOu to', loggedInOUId);
              LocalStorageService.setItem('defaultLoginOu', loggedInOUId);
            }
          }
          if (!loggedInOUId && response.accessibleOUs && response.accessibleOUs.length > 0 && response.accessibleOUs[0].ouId) {
            loggedInOUId = response.accessibleOUs[0].ouId;
            LocalStorageService.setItem('defaultLoginOu', loggedInOUId);
          }
          dispatch(saveFuelUserAccessData(response));
          if (loggedInOUId) {
            const promise = FuelPortalService.getUserPermissions(loggedInOUId).then((response2) => {
              let permissions = new Permissions([]);
              if (response2 && response2.permissions && response2.permissions.length > 0) {
                permissions = new Permissions(response2.permissions);
              }

              dispatch(saveStage(new Fetched(key, permissions)));
              dispatch(savePermissions(permissions));

              return permissions;
            });
          }
        }
        return promise;
      });
      dispatch(saveStage(new OngoingFetch(key, promise)));
      // dispatch(savePermissions(promise));
      return promise;
    };
    const generateKey = () => {
      const { id_token, access_token, expires_at, sub }: IDSUser = user;
      return [id_token, access_token, expires_at.toString(), sub].join('|');
    };
    try {
      // console.log('stage',stage instanceof NotStarted);
      if (stage instanceof NotStarted) {
        const key = generateKey();
        return await executeNewFetch(key);
      }
      if (stage instanceof Fetched) {
        const newKey = generateKey();
        const existingKey = stage.key;
        return newKey === existingKey ? dispatch(savePermissions(stage.permissions)) : await executeNewFetch(newKey);
      }
      throw new Error(`Unexpected stage!: ${stage}`);
    } catch (error) {
      // dispatch(getUserFailure());
    }
  }
);
